import {
  reactQueryDelete,
  reactQueryGet,
  reactQueryPost,
  reactQueryPut,
} from "@/react/helpers/backendRequestHelpers";
import { workflows } from "@circle-react/helpers/urlHelpers/internalApi/workflows";
import type { FetchOptions } from "../helpers/backendRequestHelpers";
import { fetchRequest } from "../helpers/backendRequestHelpers";
import { internalApi } from "../helpers/urlHelpers";

interface CreateEventOptions extends FetchOptions {
  formData: any;
}

const create = <T = unknown>({
  formData,
  ...fetchRequestOptions
}: CreateEventOptions) => {
  const { space_id } = formData;
  return fetchRequest<T>(internalApi.events.create({ spaceId: space_id }), {
    method: "POST",
    body: JSON.stringify({ event: formData }),
    ...fetchRequestOptions,
  });
};

interface UpdateEventOptions extends FetchOptions {
  event: any;
  formData: any;
}

const update = <T = unknown>({
  event,
  formData,
  ...fetchRequestOptions
}: UpdateEventOptions) => {
  const { slug, space_id } = event;
  return fetchRequest<T>(
    internalApi.events.update({ slug, spaceId: space_id }),
    {
      method: "PATCH",
      body: JSON.stringify({ event: formData }),
      ...fetchRequestOptions,
    },
  );
};

interface DestroyEventOptions {
  event: any;
  params?: Record<string, unknown>;
}

const destroy = <T = unknown>({ event, params = {} }: DestroyEventOptions) => {
  const { slug, space_id: spaceId } = event;
  return fetchRequest<T>(
    internalApi.events.destroy({ slug, spaceId, params }),
    {
      method: "DELETE",
    },
  );
};

interface RsvpOptions {
  event: any;
  params?: Record<string, unknown>;
}

const rsvp = ({ event, params = {} }: RsvpOptions) => {
  const { slug, space_id: spaceId } = event;
  return reactQueryPost(
    internalApi.events.attendees({ slug, spaceId, params }),
  );
};

interface UnRsvpOptions {
  event: any;
}

const unRsvp = ({ event }: UnRsvpOptions) => {
  const { slug, space_id: spaceId } = event;
  return reactQueryDelete(internalApi.events.attendees({ slug, spaceId }));
};

interface SearchCommunityMembersOptions {
  event: any;
  params?: Record<string, unknown>;
}

const searchCommunityMembers = ({
  event,
  params,
}: SearchCommunityMembersOptions) => {
  const { id: eventId } = event;
  return reactQueryGet(
    internalApi.eventAttendees.searchCommunityMembers({ eventId, params }),
  );
};

interface DownloadCSVOptions {
  event: any;
}

const downloadCSV = ({ event }: DownloadCSVOptions) => {
  const { slug, space_id: spaceId } = event;
  return reactQueryGet(internalApi.eventAttendees.exportCsv({ spaceId, slug }));
};

interface BulkRsvpOptions {
  event: any;
  params?: Record<string, unknown>;
}

const bulkRsvp = ({ event, params = {} }: BulkRsvpOptions) => {
  const { slug, space_id: spaceId } = event;
  const { community_member_ids, invited_entities_ids, role } = params;
  return reactQueryPut(internalApi.eventAttendees.bulkRsvp({ slug, spaceId }), {
    community_member_ids,
    invited_entities_ids,
    role,
  });
};

interface FetchRecurringOptions {
  event: any;
}

const fetchRecurring = ({ event }: FetchRecurringOptions) => {
  const { slug, space_id: spaceId } = event;
  return reactQueryGet(internalApi.recurringEvents.index({ slug, spaceId }));
};

interface RecurringRsvpOptions {
  event: any;
  params?: Record<string, unknown>;
}

const recurringRsvp = ({ event, params = {} }: RecurringRsvpOptions) => {
  const { slug, space_id: spaceId } = event;
  const { eventIds } = params;
  return reactQueryPut(internalApi.recurringEvents.rsvp({ slug, spaceId }), {
    ...params,
    event_ids: eventIds,
  });
};

interface RecurringUnRsvpOptions {
  event: any;
  params?: Record<string, unknown>;
}

const recurringUnRsvp = ({ event, params }: RecurringUnRsvpOptions) => {
  const { slug, space_id: spaceId } = event;
  return reactQueryDelete(
    internalApi.eventAttendees.index({
      slug,
      spaceId,
      params: {
        unrsvp_mode: params,
      },
    }),
  );
};

interface CreateLiveRoomForEventOptions {
  event: any;
}

const createLiveRoomForEvent = <T = unknown>({
  event,
}: CreateLiveRoomForEventOptions) => {
  const { id } = event;
  return fetchRequest<T>(internalApi.eventLiveStreams.create(), {
    method: "POST",
    body: JSON.stringify({ event_id: id }),
    forReactQuery: true,
  });
};

interface RemoveAttendeeByIdOptions {
  attendeeId: string;
  event: any;
}

const removeAttendeeById = <T = unknown>({
  event,
  attendeeId,
}: RemoveAttendeeByIdOptions) => {
  const { slug, space_id: spaceId } = event;

  return fetchRequest<T>(
    internalApi.eventAttendees.destroyById({
      slug,
      spaceId,
      attendeeId,
    }),
    {
      method: "DELETE",
    },
  );
};

interface PinOptions {
  event: any;
  formData: any;
}

const pin = <T = unknown>({ event, formData }: PinOptions) =>
  fetchRequest<T>(
    internalApi.events.pin({ spaceId: event.space_id, slug: event.slug }),
    {
      method: "PATCH",
      body: JSON.stringify({ event: formData }),
    },
  );

interface AddToCalendarOptions {
  event: any;
  params?: Record<string, unknown>;
}

const addToCalendar = ({ event, params }: AddToCalendarOptions) =>
  reactQueryGet(
    internalApi.events.addToCalendar({
      spaceId: event.space_id,
      slug: event.slug,
      params: params,
    }),
  );

interface FetchUpcomingEventsOptions {
  params?: Record<string, unknown>;
}

const fetchUpcomingEvents = ({ params = {} }: FetchUpcomingEventsOptions) =>
  reactQueryGet(internalApi.workflowsUpcomingEvents.index(params));

interface FetchEventOptions {
  id: string;
  params?: Record<string, unknown>;
}

const fetchEvent = ({ id, params = {} }: FetchEventOptions) =>
  reactQueryGet(workflows.events.show({ id: id, params: params }));

interface ListEventsOptions {
  params?: Record<string, unknown>;
}

const listEvents = ({ params = {} }: ListEventsOptions) =>
  reactQueryGet(workflows.events.index({ params: params }));

export const eventApi = {
  create,
  update,
  destroy,
  fetchEvent,
  pin,
  rsvp,
  unRsvp,
  searchCommunityMembers,
  downloadCSV,
  bulkRsvp,
  fetchRecurring,
  recurringRsvp,
  recurringUnRsvp,
  createLiveRoomForEvent,
  removeAttendeeById,
  fetchUpcomingEvents,
  listEvents,
  addToCalendar,
};
